import * as L from 'leaflet';
import { flushSync } from 'react-dom';
import type { IVehicle } from '../../../../models/labs/micromobility/vehicle.model';
import { TranslationService } from '../../../../services/translation.service';
import { ButtonDivIcon } from './button-div-icon';
import DeviceHelper, { DeviceType } from '../../../../helpers/device.helper';
import batteryLevel25Icon from '../../../../../assets/icons/micromobility-battery-level-25.svg';
import batteryLevel50Icon from '../../../../../assets/icons/micromobility-battery-level-50.svg';
import batteryLevel75Icon from '../../../../../assets/icons/micromobility-battery-level-75.svg';
import batteryLevel100Icon from '../../../../../assets/icons/micromobility-battery-level-100.svg';
import darkScooterIcon from '../../../../../assets/icons/micromobility-scooter-dark.svg';
import darkScooterMarkerBlueOutlineIcon from '../../../../../assets/icons/micromobility-scooter-dark-marker-blue-outline.svg';
import darkBikeElectricAssistIcon from '../../../../../assets/icons/micromobility-bike-electric-assist-dark.svg';
import darkBikeElectricAssistMarkerBlueOutlineIcon from '../../../../../assets/icons/micromobility-bike-electric-assist-dark-marker-blue-outline.svg';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import { FormFactor } from '../../../../models/labs/micromobility/form-factor.enum';
import { PropulsionType } from '../../../../models/labs/micromobility/propulsion-type.enum';

export default class DocklessVehicleClusterMarker extends L.Marker {
  constructor(vehicle: IVehicle, isHighlighted: boolean = false) {
    super([vehicle.latitude, vehicle.longitude]);

    const defaultIcon = isHighlighted
      ? new ButtonDivIcon({
          className: 'button-div-icon micromobility-marker--clicked',
          iconSize: [30, 38],
          iconAnchor: [15, 38],
          popupAnchor: [0, -10],
          html: this.createMarkerHtml(vehicle, false, isHighlighted),
        })
      : new ButtonDivIcon({
          className: 'button-div-icon micromobility-marker d-flex flex-row align-items-center justify-content-center bg-white border border-blue-1000',
          iconSize: [24, 24],
          iconAnchor: [12, 12],
          popupAnchor: [0, -10],
          html: this.createMarkerHtml(vehicle, false, isHighlighted),
        });

    this.setIcon(defaultIcon);

    this.bindPopup(this.createPopupHtml(vehicle), {
      autoPan: true,
      offset: [0, -22],
    });

    this.on('popupopen', (popupEvent: L.PopupEvent) => {
      const trackingAction = vehicle.formFactor === FormFactor.Bicycle && vehicle.propulsionType === PropulsionType.ElectricAssist ? 'Klick på elcykel' : 'Klick på elsparkcykel';
      AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, trackingAction, vehicle.operatorName);

      // Force a synchronous update to ensure the popup is rendered before we try to focus it
      flushSync(() => {});

      // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
      // Focus on the opened popup
      const popups = popupEvent.popup.getElement()?.getElementsByClassName('micromobility-popup');
      if (!popups || popups.length === 0) {
        return;
      }
      for (let i = 0; i < popups.length; i += 1) {
        const popup = popups[i] as HTMLElement;
        popup.setAttribute('tabindex', '-1');
        popup.style.outline = 'none'; // Workaround for hiding built-in focus marker, see NV-4097
        popup.focus();
      }

      // Add the click event listener to the button in the popup
      const deviceType = DeviceHelper.getDeviceType();
      const deviceSrc = deviceType === DeviceType.Android ? vehicle.rentalUriAndroid : vehicle.rentalUriIos;
      const buttons = popupEvent.popup.getElement()?.getElementsByClassName('micromobility-popup__link');
      if (buttons && buttons.length > 0) {
        const button = buttons[0] as HTMLButtonElement;
        button.addEventListener('click', () => this.openAppOrRedirect(deviceSrc, vehicle.operatorName));
      }

      // Fix the a11y attributes on the close button
      const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button');
      if (closeButtons && closeButtons.length > 0) {
        const closeButton = closeButtons[0] as HTMLLinkElement;
        closeButton.setAttribute('role', 'button');
        closeButton.setAttribute('aria-label', TranslationService.get('common.map.closepopup'));
        closeButton.href = '#';
      }

      this.setIcon(this.createClickedIcon(vehicle, isHighlighted));
      this.setZIndexOffset(1000);
    });

    this.on('popupclose', () => {
      this.setIcon(defaultIcon);
      this.setZIndexOffset(0);
    });

    if (isHighlighted) {
      setTimeout(() => {
        this.openPopup();
      }, 500);
    }
  }

  private createClickedIcon(vehicle: IVehicle, isHighlighted: boolean) {
    return new ButtonDivIcon({
      className: 'button-div-icon micromobility-marker--clicked',
      iconSize: [30, 38],
      iconAnchor: [15, 38],
      popupAnchor: [0, -10],
      html: this.createMarkerHtml(vehicle, true, isHighlighted),
    });
  }

  private createMarkerHtml(vehicle: IVehicle, isClicked: boolean, isHighlighted: boolean): string {
    let icon;
    if (vehicle.formFactor === FormFactor.Bicycle && vehicle.propulsionType === PropulsionType.ElectricAssist) {
      icon = isClicked || isHighlighted ? darkBikeElectricAssistMarkerBlueOutlineIcon : darkBikeElectricAssistIcon;
    } else {
      icon = isClicked || isHighlighted ? darkScooterMarkerBlueOutlineIcon : darkScooterIcon;
    }
    const altText =
      vehicle.formFactor === FormFactor.Bicycle && vehicle.propulsionType === PropulsionType.ElectricAssist
        ? TranslationService.get(`travelplanner.micromobility.formfactor.bicycle.electricassist.singular`)
        : TranslationService.get(`travelplanner.micromobility.formfactor.${vehicle.formFactor}.singular`);
    const className = isClicked || isHighlighted ? 'micromobility-marker--clicked__icon w-100 h-100' : 'micromobility-marker__icon';

    return `<img src="${DataUrlHelper.encode(icon)}" alt="${altText}" class="${className}" />`;
  }

  private creteRangeIconSrc(vehicle: IVehicle): string {
    const currentRangePercentage = Math.floor((vehicle.currentRangeInMeters / vehicle.maxRangeInMeters) * 100);
    if (currentRangePercentage >= 90) return DataUrlHelper.encode(batteryLevel100Icon);
    if (currentRangePercentage >= 60) return DataUrlHelper.encode(batteryLevel75Icon);
    if (currentRangePercentage >= 25) return DataUrlHelper.encode(batteryLevel50Icon);
    return DataUrlHelper.encode(batteryLevel25Icon);
  }

  private createRangeText(vehicle: IVehicle): string {
    return `${Math.floor(vehicle.currentRangeInMeters / 1000)} km ${TranslationService.get('travelplanner.micromobility.range').toLowerCase()}`;
  }

  // NV-5814: Special solution for different operators deeplinks that open their app or redirect to the app store
  private openAppOrRedirect(deviceSrc: string, operatorName: string) {
    AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Klick på boka i popup', operatorName);
    // Try to open the app
    const newWindow = window.open(deviceSrc, '_blank');
    // Check if the app was not opened after a short delay
    setTimeout(() => {
      // If the new window is still open and it is not empty the app was not opened (e.g. the app is not installed)
      // Special case for bolt, when app is not istalled on device. other operators will be redirected to the app store
      if (newWindow && !newWindow.closed && deviceSrc.toLowerCase().includes('bolt')) {
        const redirectUrl =
          DeviceHelper.getDeviceType() === DeviceType.Android ? 'https://play.google.com/store/apps/details?id=ee.mtakso.client' : 'https://apps.apple.com/se/app/bolt-request-a-ride/id675033630';
        newWindow.location.href = redirectUrl;
      }
    }, 300);
  }

  private createLinkHtml(vehicle: IVehicle): string | undefined {
    const deviceType = DeviceHelper.getDeviceType();
    if (![DeviceType.Android, DeviceType.iOS].includes(deviceType)) {
      return undefined;
    }

    const button = document.createElement('button');
    button.className = 'btn--as-link micromobility-popup__link mt-2 w-100 text-start';
    button.innerHTML = `<span class='link--external'>${TranslationService.get('travelplanner.micromobility.bookwith')} ${vehicle.operatorName}</span>`;
    return button.outerHTML;
  }

  private createPopupHtml(vehicle: IVehicle): string {
    const operatorTranslationKey = vehicle.operatorName.toLowerCase();
    const offerText = TranslationService.get(`travelplanner.micromobility.${operatorTranslationKey}.offerinformationtext`);
    const linkHtml = this.createLinkHtml(vehicle);
    const moreInfo = linkHtml ? undefined : TranslationService.get(`travelplanner.micromobility.${operatorTranslationKey}.moreinfo`);
    const vehicleAvailable = vehicle.vehicleAvailable !== false;
    const vehicleTypeName =
      vehicle.formFactor === FormFactor.Bicycle && vehicle.propulsionType === PropulsionType.ElectricAssist
        ? TranslationService.get(`travelplanner.micromobility.formfactor.bicycle.electricassist.singular`)
        : TranslationService.get(`travelplanner.micromobility.formfactor.${vehicle.formFactor}.singular`);

    return `
      <div class="micromobility-popup">
        <h3 class="micromobility-popup__name">
          ${vehicleTypeName} (${vehicle.operatorName})
        </h3>
        <div class="micromobility-popup__range d-flex flex-row">
          <img src="${this.creteRangeIconSrc(vehicle)}" alt="" class="micromobility-popup__range-icon me-1" />
          <span>${this.createRangeText(vehicle)}</span>
        </div>
        ${!vehicleAvailable ? `<div class="mb-1 micromobility-search__warning-text">${TranslationService.get('travelplanner.micromobility.vehiclenolongeravailable')}</div>` : ''}
        ${offerText && vehicleAvailable ? `<div class="micromobility-popup__offer-text mt-2 px-1">${offerText}</div>` : ''}
        ${moreInfo && vehicleAvailable ? `<div class="micromobility-popup__more-info mt-2 text-end">${moreInfo}</div>` : ''}
        ${linkHtml && vehicleAvailable ? linkHtml : ''}
      </div>
    `;
  }
}
